import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  SalesOrderEvent,
  SalesOrderEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/trade/salesOrder_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { SalesOrderCreatedDetail } from './SalesOrderCreatedDetail';
import { SalesOrderDeletedDetail } from './SalesOrderDeletedDetail';
import { SalesOrderStatusTransitionDetail } from './SalesOrderStatusTransitionDetail';
import { SalesOrderUpdatedDetail } from './SalesOrderUpdatedDetail';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

const salesOrderStatusEventLabels = {
  opened: 'Open',
  finished: 'Finished',
  voided: 'Voided',
} as const;

type SalesOrderEventDetailProps = {
  payload: string;
};

export const SalesOrderEventDetail = ({
  payload,
}: SalesOrderEventDetailProps) => {
  const t = useTranslations();
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): SalesOrderEvent | null => {
    try {
      return fromJson(SalesOrderEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return (
      <JsonViewer data={payload} title={t.eventLogs.rawPayloadParseError} />
    );
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <SalesOrderCreatedDetail event={inner.value} />;
      case 'updated':
        return <SalesOrderUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <SalesOrderDeletedDetail event={inner.value} />;
      case 'opened':
      case 'finished':
      case 'voided':
        return (
          <SalesOrderStatusTransitionDetail
            salesOrder={inner.value.salesOrder}
            previousStatus={inner.value.previousStatus}
            newStatusLabel={salesOrderStatusEventLabels[inner.case]}
          />
        );
      case undefined:
        return (
          <JsonViewer data={payload} title={t.eventLogs.unknownEventType} />
        );
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return t.eventLogs.salesOrder.events.created;
      case 'updated':
        return t.eventLogs.salesOrder.events.updated;
      case 'deleted':
        return t.eventLogs.salesOrder.events.deleted;
      case 'opened':
        return t.eventLogs.salesOrder.events.opened;
      case 'finished':
        return t.eventLogs.salesOrder.events.finished;
      case 'voided':
        return t.eventLogs.salesOrder.events.voided;
      case undefined:
        return t.eventLogs.unknownEvent;
    }
  };

  return (
    <Col className="@SalesOrderEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
